import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo } from 'react';
import { Check, X, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export function MultiSelectSearch({
  label,
  placeholder,
  options,
  selected,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  useLanguage();
  const [q, setQ] = useState('');
  
  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())),
    [options, q]
  );

  const selectedOptions = useMemo(
    () => options.filter((o) => selected.includes(o.value)),
    [options, selected]
  );

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <div className="space-y-2">
      {label && <label className="text-[13px] font-semibold text-[#374151]">{label}</label>}
      <div className="rounded-xl border border-[#EEF2F7] bg-white overflow-hidden flex flex-col">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 h-12 border-b border-[#EEF2F7] flex-shrink-0 bg-white z-10">
          <Search className="w-4 h-4 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#9CA3AF] font-medium"
          />
        </div>

        {/* Selected Chips Container */}
        {selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-[6px] p-2.5 border-b border-[#EEF2F7] max-h-[100px] overflow-y-auto bg-white">
            {selectedOptions.map((opt) => (
              <div 
                key={opt.value} 
                className="inline-flex items-center gap-[6px] h-[28px] px-3 rounded-lg bg-[#F0F5FF] text-[#2563EB] text-[12px] font-bold border border-[#D1E0FF] animate-in fade-in zoom-in duration-200 shadow-sm"
              >
                {opt.label}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.value);
                  }} 
                  className="inline-flex items-center justify-center opacity-75 hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Options List */}
        <ScrollArea className="h-48">
          <div className="p-1">
            {filtered.length > 0 ? (
              filtered.map((opt) => {
                const active = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 h-10 rounded-lg text-[13px] transition-all group',
                      active 
                        ? 'bg-[#F0F5FF] text-[#2563EB] font-bold' 
                        : 'text-[#374151] hover:bg-[#F9FAFB] font-medium'
                    )}
                  >
                    <span className="truncate">{opt.label}</span>
                    {active && <Check className="w-3.5 h-3.5 text-[#2563FF]" />}
                  </button>
                );
              })
            ) : (
              <div className="p-8 text-center flex flex-col items-center justify-center gap-2">
                <p className="text-[13px] text-[#9CA3AF] font-medium">
                  {q.trim() ? translateInterfaceText("Nenhum resultado encontrado") : translateInterfaceText("Regiões ainda não cadastradas para este país.")}
                </p>
                {!q.trim() && (
                  <p className="text-[11px] text-[#9CA3AF]">{translateInterfaceText("Você ainda pode aplicar a regra para o país inteiro.")}</p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}